/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import IEnergyManagementProps, {
  IAny
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import EmaasEnergyUsagesAllUtilityDetailWidget from '../AllUtilitiesDetails/EmaasEnergyUsagesAllUtilityDetailWidget';
import EnergyScopeSavingsByMeterComponent from '../ElectricityDetails/EnergyScopeSavingsByMeterWidget/EnergyScopeSavingsByMeterComponent';
import {
  EnergyUsageScopeType,
  EnergyUsageUtilityType
} from '../../../shared/Utils/Constants';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getIntervalType } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import moment from 'moment';
import NoData from '../../../shared/ErrorComponents/NoData';
import EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI from '../AllUtilitiesDetails/EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI';
import { ApiContext } from '../../../../context/ApiContext';
import { EmaasSubscriptionType } from '../../../shared/Enums';
import EnergyScopeSavingsByMeterComponentSeparateAPI from '../ElectricityDetails/EnergyScopeSavingsByMeterWidget/EnergyScopeSavingsByMeterComponentSeparateAPI';

const EmassEnergyUsagesWaterDetails = (props: IEnergyManagementProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext } = props;

  const apiContext = useContext(ApiContext);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const [tableData, setTableData] = useState<IAny>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const { getEnergyUsageTableData } = apiContext;
  const getTableData = async () => {
    try {
      setLoading(true);
      if (getEnergyUsageTableData) {
        const tableData: any = await getEnergyUsageTableData(t(d.water));
        if (tableData && tableData.data) {
          setTableData(tableData.data);
        } else {
          setError({ name: 'No Data Found' });
        }
        setStatusCode(tableData.status);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setStatusCode(500);
    }
  };
  useEffect(() => {
    getTableData();
  }, []);

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: isSitePremium ? 'emaas-energy-usage-summary' : 'emaas-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: getIntervalType(startDate, endDate)
    }
  };

  const connectorConfigPerformanceAggregation = {
    name: 'emaas-energy-usage-performance-aggregation',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: getIntervalType(startDate, endDate)
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorPerformanceAggregation: any;
  dataConnectorPerformanceAggregation = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigPerformanceAggregation,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];

  const connectorConfigBreakdownByZone = {
    name: 'emaas-energy-usage-breakdown-by-zone',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: getIntervalType(startDate, endDate),
      groupType: 'Area'
    }
  };

  let dataConnectorBreakdownByZone: IAny;

  dataConnectorBreakdownByZone = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigBreakdownByZone,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();

        if (isSitePremium) {
          dataConnectorPerformanceAggregation &&
            dataConnectorPerformanceAggregation.actions &&
            dataConnectorPerformanceAggregation.actions.getAll();

          dataConnectorBreakdownByZone &&
            dataConnectorBreakdownByZone.actions &&
            dataConnectorBreakdownByZone.actions.getAll();
        }
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  const UtilityBillType = EnergyUsageUtilityType.Water;
  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <div className="all-utilities-details">
      {isSitePremium ? (
        <EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI
          dataConnector={dataConnector}
          dataConnectorPerformanceAggregation={
            dataConnectorPerformanceAggregation
          }
          headerTitle={t(d.WaterOverview)}
          requestContext={requestContext}
        />
      ) : (
        <EmaasEnergyUsagesAllUtilityDetailWidget
          dataConnector={dataConnector}
          headerTitle={t(d.WaterOverview)}
          requestContext={requestContext}
        />
      )}
      {
        <EnergyScopeSavingsByMeterComponent
          dataConnector={
            isSitePremium ? dataConnectorBreakdownByZone : dataConnector
          }
          UtilityBillType={UtilityBillType}
          utilityType={EnergyUsageScopeType.WATER}
          subscriptionType={requestContext.context.subscriptionType!}
          toggleSeparateApi={apiContext.toggleSeparateApi}
        />
      }
    </div>
  );
};
export default EmassEnergyUsagesWaterDetails;
